/**
 * The palette's scorer (PLAN §3.4, API §12.1): fzf / VS Code style matching,
 * case- and diacritic-insensitive, with highlight ranges in the ORIGINAL
 * string's UTF-16 offsets.
 *
 * Tiers for one term against one field (0..1000):
 *   exact field ........................................ 1000
 *   prefix of the field ................................ 920–950
 *   a whole word of the field .......................... 900
 *   prefix of a later word ............................. 820–870
 *   word-start subsequence ("kk" → Kenan Kobic) ........ 600–850
 *   contiguous inside a word ........................... 560–600
 *   scattered subsequence (fuzzy fields only) .......... 200–550
 * Every term must match some field (AND, as chat search). The query's score
 * is the mean of its terms' best weighted scores. Names match in any word
 * order because every term is matched on its own.
 */

const SPECIAL = {
  ß: 'ss',
  ẞ: 'ss',
  đ: 'dj',
  Đ: 'dj',
  æ: 'ae',
  Æ: 'ae',
  œ: 'oe',
  Œ: 'oe',
  ø: 'o',
  Ø: 'o',
  ł: 'l',
  Ł: 'l',
  þ: 'th',
  Þ: 'th',
  ı: 'i',
};
const MARKS = /[̀-ͯ᪰-᫿᷀-᷿⃐-⃿︠-︯]/g;

const charCache = new Map();
function foldChar(ch) {
  let r = charCache.get(ch);
  if (r !== undefined) return r;
  if (SPECIAL[ch]) r = SPECIAL[ch];
  else {
    r = ch.normalize('NFKD').replace(MARKS, '').toLowerCase();
    // A character that decomposes into nothing but marks keeps itself.
    if (!r) r = ch.toLowerCase();
  }
  if (charCache.size < 4096) charCache.set(ch, r);
  return r;
}

function fold(s) {
  const src = String(s ?? '');
  const out = [];
  const starts = [];
  const ends = [];
  for (let i = 0; i < src.length; ) {
    const cp = src.codePointAt(i);
    const len = cp > 0xffff ? 2 : 1;
    const ch = src.slice(i, i + len);
    const r = foldChar(ch);
    for (let k = 0; k < r.length; k += 1) {
      out.push(r[k]);
      starts.push(i);
      ends.push(i + len);
    }
    i += len;
  }
  return { norm: out.join(''), starts, ends };
}

/** NFKD, marks stripped, lower-case, ß→ss, đ→dj, æ→ae. */
export function normalize(s) {
  const src = String(s ?? '');
  // Fast path: plain ASCII only needs lower-casing.
  // eslint-disable-next-line no-control-regex
  if (/^[\x00-\x7f]*$/.test(src)) return src.toLowerCase();
  return fold(src).norm;
}

const ALNUM_RE = /[\p{L}\p{N}]/u;
function isAlnum(c) {
  const code = c.charCodeAt(0);
  if (code < 128) return (code >= 48 && code <= 57) || (code >= 97 && code <= 122) || (code >= 65 && code <= 90);
  return ALNUM_RE.test(c);
}

/** A 28-bit "which characters occur" mask: a–z, any digit, any other letter. */
function charMask(str) {
  let m = 0;
  for (let i = 0; i < str.length; i += 1) {
    const c = str.charCodeAt(i);
    if (c >= 97 && c <= 122) m |= 1 << (c - 97);
    else if (c >= 48 && c <= 57) m |= 1 << 26;
    else if (c > 127) m |= 1 << 27;
  }
  return m;
}

const termCache = new Map();
function normTerm(t) {
  let n = termCache.get(t);
  if (n === undefined) {
    n = normalize(t);
    if (termCache.size > 2000) termCache.clear();
    termCache.set(t, n);
  }
  return n;
}

const prepCache = new Map();
const PREP_CACHE_MAX = 50000;

/**
 * Cached normalised form plus the index map and word starts.
 * @returns {{ text: string, norm: string, starts: number[], ends: number[], ws: Uint8Array }}
 */
export function prepare(s) {
  const text = String(s ?? '');
  const hit = prepCache.get(text);
  if (hit) return hit;
  const { norm, starts, ends } = fold(text);
  const ws = new Uint8Array(norm.length);
  for (let i = 0; i < norm.length; i += 1) {
    const c = norm[i];
    if (!isAlnum(c)) continue;
    if (i === 0) {
      ws[i] = 1;
      continue;
    }
    const prev = norm[i - 1];
    if (!isAlnum(prev)) ws[i] = 1;
    else if (starts[i] !== starts[i - 1]) {
      // camelCase / digit boundary, read on the original characters.
      const o = text[starts[i]];
      const po = text[starts[i - 1]];
      if (o !== o.toLowerCase() && po === po.toLowerCase() && po !== po.toUpperCase()) ws[i] = 1;
      else if (/\d/.test(o) !== /\d/.test(po)) ws[i] = 1;
    }
  }
  let wsChars = '';
  for (let i = 0; i < norm.length; i += 1) if (ws[i]) wsChars += norm[i];
  const p = { text, norm, starts, ends, ws, wsChars, mask: charMask(norm), flat: norm.replace(/\s+/g, ' ').trim() };
  if (prepCache.size >= PREP_CACHE_MAX) prepCache.clear();
  prepCache.set(text, p);
  return p;
}

function wordEndsAt(P, end) {
  return end >= P.norm.length || !isAlnum(P.norm[end]);
}

function wordIndexOf(P, pos) {
  let k = 0;
  for (let i = 0; i < pos; i += 1) if (P.ws[i]) k += 1;
  return k;
}

/** Word-start subsequence: every run of the term starts on a word start. */
function wordSegments(term, P) {
  const n = P.norm;
  const ranges = [];
  let pos = -1;
  let ti = 0;
  let runStart = -1;
  while (ti < term.length) {
    const c = term[ti];
    if (pos >= 0 && pos + 1 < n.length && n[pos + 1] === c && runStart >= 0) {
      pos += 1;
      ti += 1;
      continue;
    }
    let j = pos + 1;
    while (j < n.length && !(P.ws[j] && n[j] === c)) j += 1;
    if (j >= n.length) return null;
    if (runStart >= 0) ranges.push([runStart, pos + 1]);
    runStart = j;
    pos = j;
    ti += 1;
  }
  ranges.push([runStart, pos + 1]);
  return ranges;
}

/** Scattered subsequence with a bounded span. */
function scattered(term, P) {
  const n = P.norm;
  const first = n.indexOf(term[0]);
  if (first < 0) return null;
  const ranges = [];
  let pos = first;
  let runStart = first;
  let gaps = 0;
  let gapLen = 0;
  for (let ti = 1; ti < term.length; ti += 1) {
    const j = n.indexOf(term[ti], pos + 1);
    if (j < 0) return null;
    if (j !== pos + 1) {
      ranges.push([runStart, pos + 1]);
      runStart = j;
      gaps += 1;
      gapLen += j - pos - 1;
    }
    pos = j;
  }
  ranges.push([runStart, pos + 1]);
  const span = pos - first + 1;
  if (span > Math.max(16, term.length * 3)) return null;
  return { ranges, gaps, gapLen, startsWord: !!P.ws[first] };
}

/** One normalised term against one prepared field → { score, ranges } | null. */
function scoreTerm(term, P, fuzzy) {
  const n = P.norm;
  if (!term || !n) return null;
  const cov = term.length / n.length;
  if (n === term) return { score: 1000, ranges: [[0, n.length]] };
  let at = n.indexOf(term);
  if (at === 0) return { score: Math.min(950, 920 + Math.round(30 * cov)), ranges: [[0, term.length]] };
  let contiguous = null;
  while (at > 0) {
    if (P.ws[at]) {
      const end = at + term.length;
      if (wordEndsAt(P, end)) return { score: 900, ranges: [[at, end]] };
      const k = wordIndexOf(P, at);
      return { score: Math.max(820, 850 + Math.round(20 * cov) - Math.min(30, k * 3)), ranges: [[at, end]] };
    }
    if (!contiguous) contiguous = at;
    at = n.indexOf(term, at + 1);
  }
  if (term.length >= 2 && P.wsChars.indexOf(term[0]) >= 0) {
    const seg = wordSegments(term, P);
    if (seg) return { score: Math.max(600, 850 - 40 * (seg.length - 1)), ranges: seg };
  }
  if (contiguous != null) {
    return { score: Math.min(600, 560 + Math.round(40 * cov)), ranges: [[contiguous, contiguous + term.length]] };
  }
  if (fuzzy && term.length >= 2) {
    const s = scattered(term, P);
    if (s) {
      const score = 550 - 25 * s.gaps - 2 * s.gapLen + (s.startsWord ? 20 : 0);
      return { score: Math.max(200, Math.min(550, score)), ranges: s.ranges };
    }
  }
  return null;
}

function toOriginal(P, ranges) {
  const out = [];
  for (const [a, b] of ranges) {
    if (b <= a) continue;
    out.push({ start: P.starts[a], end: P.ends[b - 1] });
  }
  return mergeRanges(out);
}

/** Sort and merge overlapping / adjacent ranges. */
export function mergeRanges(ranges) {
  const rs = (ranges || []).filter((r) => r && r.end > r.start).map((r) => ({ start: r.start, end: r.end }));
  if (!rs.length) return [];
  rs.sort((a, b) => a.start - b.start || a.end - b.end);
  const out = [rs[0]];
  for (const r of rs.slice(1)) {
    const last = out[out.length - 1];
    if (r.start <= last.end) last.end = Math.max(last.end, r.end);
    else out.push(r);
  }
  return out;
}

/** Normalise a query's terms once (per keystroke, not per row). */
export function prepareTerms(terms) {
  const ts = [];
  const masks = [];
  for (const t of terms || []) {
    const n = normTerm(t);
    if (n) {
      ts.push(n);
      masks.push(charMask(n));
    }
  }
  return { ts, masks, joined: ts.join(' ') };
}

/**
 * The scorer over prepared terms. `scoreOnly` skips the highlight ranges
 * (candidate selection over thousands of rows).
 */
export function scorePrepared(pt, fields, scoreOnly = false) {
  const { ts, masks } = pt;
  if (!ts.length) return scoreOnly ? 0 : { score: 0, matches: [] };
  const byField = scoreOnly ? null : new Map();
  let total = 0;
  const nf = fields ? fields.length : 0;
  for (let ti = 0; ti < ts.length; ti += 1) {
    const term = ts[ti];
    const tm = masks[ti];
    let bestW = -1;
    let bestF = null;
    let bestR = null;
    for (let fi = 0; fi < nf; fi += 1) {
      const f = fields[fi];
      if (!f || !f.prepared) continue;
      // Every match type needs every character of the term in that field.
      if ((f.prepared.mask & tm) !== tm) continue;
      const r = scoreTerm(term, f.prepared, f.name === 'title' || f.name === 'keyLabel');
      if (!r) continue;
      const weighted = r.score * (f.weight ?? 1);
      if (weighted > bestW) {
        bestW = weighted;
        bestF = f;
        bestR = r.ranges;
      }
    }
    if (!bestF) return null;
    total += bestW;
    if (scoreOnly) continue;
    const cur = byField.get(bestF.name) || { field: bestF, ranges: [] };
    cur.ranges.push(...bestR);
    byField.set(bestF.name, cur);
  }
  let score = total / ts.length;
  // The whole query equals a field ("kenan kobic" = the name) → that field's exact score.
  if (ts.length > 1) {
    for (let fi = 0; fi < nf; fi += 1) {
      const f = fields[fi];
      if (f && f.prepared && f.prepared.flat === pt.joined) score = Math.max(score, 1000 * (f.weight ?? 1));
    }
  }
  if (scoreOnly) return Math.round(score);
  const matches = [];
  for (const { field, ranges } of byField.values()) {
    matches.push({ field: field.name, ranges: toOriginal(field.prepared, ranges) });
  }
  return { score: Math.round(score), matches };
}

/**
 * @param {string[]} terms  raw terms (normalised here)
 * @param {{ prepared: ReturnType<typeof prepare>, weight: number, name: string }[]} fields
 * @returns {{ score: number, matches: { field: string, ranges: {start:number,end:number}[] }[] } | null}
 */
export function scoreTerms(terms, fields, { scoreOnly = false } = {}) {
  return scorePrepared(prepareTerms(terms), fields, scoreOnly);
}

/** Split `text` into [{ text, hit }] along merged ranges. */
export function highlightSegments(text, ranges) {
  const s = String(text ?? '');
  if (!s) return [];
  const rs = mergeRanges(ranges).filter((r) => r.start < s.length);
  if (!rs.length) return [{ text: s, hit: false }];
  const out = [];
  let pos = 0;
  for (const r of rs) {
    const a = Math.max(r.start, pos);
    const b = Math.min(r.end, s.length);
    if (a > pos) out.push({ text: s.slice(pos, a), hit: false });
    if (b > a) out.push({ text: s.slice(a, b), hit: true });
    pos = Math.max(pos, b);
  }
  if (pos < s.length) out.push({ text: s.slice(pos), hit: false });
  return out;
}
